import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
	AlertCircle,
	Briefcase,
	Mail,
	MapPin,
	Phone,
	User,
	X,
} from 'react-feather';
import { createClient, fetchClientById } from '../../slices/clientSlice';
import { formatPhoneNumberInput, removePhoneFormatting } from '../../hooks/phoneFormat';
import useGooglePlacesAutocomplete from '../../hooks/useGooglePlacesAutocomplete';
import { parseGooglePlaceAddress } from '../../utils/addressUtils';

function getClientDisplayName(client) {
	return `${client?.firstName || ''} ${client?.lastName || ''}`.trim() || 'Existing client';
}

export default function ClientHubCreateClientPanel({
	clients = [],
	currentUserId,
	onCancel,
	onClientReady,
	variant = 'sheet',
	eyebrow = 'Client Intake',
	title = 'Create or Find a Client',
	description = 'Search by phone first, prevent duplicates, and move straight into the client workspace.',
	cancelLabel = 'Cancel',
	submitLabel = 'Create Client',
	submittingLabel = 'Saving Client...',
	duplicateSubmitLabel = 'Use Existing Client',
	exactDuplicateActionLabel = 'Use Existing Client',
}) {
	const dispatch = useDispatch();
	const [submitError, setSubmitError] = useState('');
	const {
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { errors, isSubmitting },
	} = useForm({
		defaultValues: {
			firstName: '',
			lastName: '',
			companyName: '',
			email: '',
			phoneNumber: '',
			address: '',
			city: '',
			state: '',
			postalCode: '',
		},
	});
	const addressField = register('address');

	const phoneNumber = watch('phoneNumber') || '';
	const emailValue = (watch('email') || '').trim().toLowerCase();
	const firstNameValue = (watch('firstName') || '').trim().toLowerCase();
	const lastNameValue = (watch('lastName') || '').trim().toLowerCase();
	const companyValue = (watch('companyName') || '').trim().toLowerCase();

	const normalizedPhone = removePhoneFormatting(phoneNumber);

	const handlePlaceSelected = useCallback(
		(place) => {
			const parsedAddress = parseGooglePlaceAddress(place);

			setValue('address', parsedAddress.address, {
				shouldDirty: true,
				shouldValidate: true,
			});
			setValue('city', parsedAddress.city, {
				shouldDirty: true,
				shouldValidate: true,
			});
			setValue('state', parsedAddress.state, {
				shouldDirty: true,
				shouldValidate: true,
			});
			setValue('postalCode', parsedAddress.postalCode, {
				shouldDirty: true,
				shouldValidate: true,
			});
		},
		[setValue]
	);

	const { attachInputRef, autocompleteError } = useGooglePlacesAutocomplete({
		onPlaceSelected: handlePlaceSelected,
		enabled: true,
	});

	const exactDuplicate = useMemo(() => {
		if (!normalizedPhone && !emailValue) {
			return null;
		}

		return (
			clients.find((client) => {
				const clientPhone = removePhoneFormatting(client?.phoneNumber || '');
				const clientEmail = (client?.email || '').trim().toLowerCase();

				const samePhone =
					normalizedPhone.length >= 10 && clientPhone === normalizedPhone;
				const sameEmail = Boolean(emailValue) && clientEmail === emailValue;

				return samePhone || sameEmail;
			}) || null
		);
	}, [clients, emailValue, normalizedPhone]);

	const suggestedMatches = useMemo(() => {
		const query = `${firstNameValue} ${lastNameValue} ${companyValue}`.trim();
		const queryPhone = normalizedPhone;

		if (!query && queryPhone.length < 3) {
			return [];
		}

		return clients
			.filter((client) => {
				if (exactDuplicate?.id === client.id) {
					return false;
				}

				const fullName = `${client?.firstName || ''} ${client?.lastName || ''}`
					.trim()
					.toLowerCase();
				const companyName = (client?.companyName || '').toLowerCase();
				const clientPhone = removePhoneFormatting(client?.phoneNumber || '');

				return (
					(query && (fullName.includes(query) || companyName.includes(query))) ||
					(queryPhone.length >= 3 && clientPhone.includes(queryPhone))
				);
			})
			.slice(0, 4);
	}, [
		clients,
		companyValue,
		exactDuplicate?.id,
		firstNameValue,
		lastNameValue,
		normalizedPhone,
	]);

	const handlePhoneChange = (event) => {
		setValue('phoneNumber', formatPhoneNumberInput(event.target.value), {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const activateExistingClient = async (clientId) => {
		setSubmitError('');
		try {
			await dispatch(fetchClientById(clientId)).unwrap();
			onClientReady?.(clientId, { reusedExisting: true });
		} catch (error) {
			setSubmitError(
				error?.message ||
					error?.response?.data?.message ||
					'Unable to load the existing client right now.'
			);
		}
	};

	const onSubmit = async (data) => {
		setSubmitError('');

		if (exactDuplicate?.id) {
			await activateExistingClient(exactDuplicate.id);
			return;
		}

		const payload = {
			firstName: data.firstName?.trim(),
			lastName: data.lastName?.trim(),
			companyName: data.companyName?.trim(),
			email: data.email?.trim(),
			phoneNumber: removePhoneFormatting(data.phoneNumber),
			source: currentUserId,
			address: {
				address: data.address?.trim(),
				city: data.city?.trim(),
				state: data.state?.trim(),
				postalCode: data.postalCode?.trim(),
			},
			notes: [],
		};

		try {
			const createdClient = await dispatch(createClient(payload)).unwrap();
			await dispatch(fetchClientById(createdClient.id)).unwrap();
			onClientReady?.(createdClient.id, { reusedExisting: false });
		} catch (error) {
			setSubmitError(
				error?.message ||
					error?.response?.data?.message ||
					(typeof error === 'string' ? error : '') ||
					'Unable to save the client right now.'
			);
		}
	};

	return (
		<div
			className={`client-hub-sheet ${
				variant === 'embedded' ? 'client-hub-sheet--embedded' : ''
			}`}
		>
			<div className="client-hub-sheet__header">
				<div>
					<p className="client-hub-sheet__eyebrow">{eyebrow}</p>
					<h2>{title}</h2>
					<p>{description}</p>
				</div>
				<button
					type="button"
					onClick={onCancel}
					className="client-hub-sheet__close"
					aria-label="Close client panel"
				>
					<X size={20} />
				</button>
			</div>

			{exactDuplicate && (
				<div className="client-hub-alert client-hub-alert--warning">
					<div className="client-hub-alert__copy">
						<AlertCircle size={18} />
						<div>
							<strong>Existing client found.</strong>
							<p>
								{getClientDisplayName(exactDuplicate)} already uses this phone
								or email, so we will reuse that record instead of creating a
								duplicate.
							</p>
						</div>
					</div>
					<button
						type="button"
						className="client-hub-alert__action"
						onClick={() => activateExistingClient(exactDuplicate.id)}
					>
						{exactDuplicateActionLabel}
					</button>
				</div>
			)}

			{!exactDuplicate && suggestedMatches.length > 0 && (
				<div className="client-hub-suggestions">
					<p className="client-hub-suggestions__title">Possible matches</p>
					<div className="client-hub-suggestions__list">
						{suggestedMatches.map((client) => (
							<button
								type="button"
								key={client.id}
								className="client-hub-suggestion-card"
								onClick={() => activateExistingClient(client.id)}
							>
								<span className="client-hub-suggestion-card__name">
									{getClientDisplayName(client)}
								</span>
								<span className="client-hub-suggestion-card__meta">
									{client.companyName || 'No company'} ·{' '}
									{formatPhoneNumberInput(client.phoneNumber || '')}
								</span>
							</button>
						))}
					</div>
				</div>
			)}

			{submitError && (
				<div className="client-management-inline-error">{submitError}</div>
			)}

			<form className="client-hub-form" onSubmit={handleSubmit(onSubmit)}>
				<div className="client-hub-form__grid">
					<label className="client-hub-form__field">
						<span>First Name</span>
						<div className="client-hub-form__input">
							<User size={16} />
							<input
								{...register('firstName', {
									validate: (value) =>
										exactDuplicate?.id ||
										Boolean(value?.trim()) ||
										'First name is required',
								})}
								placeholder="John"
							/>
						</div>
						{errors.firstName && <p>{errors.firstName.message}</p>}
					</label>

					<label className="client-hub-form__field">
						<span>Last Name</span>
						<div className="client-hub-form__input">
							<User size={16} />
							<input
								{...register('lastName', {
									validate: (value) =>
										exactDuplicate?.id ||
										Boolean(value?.trim()) ||
										'Last name is required',
								})}
								placeholder="Doe"
							/>
						</div>
						{errors.lastName && <p>{errors.lastName.message}</p>}
					</label>

					<label className="client-hub-form__field">
						<span>Company</span>
						<div className="client-hub-form__input">
							<Briefcase size={16} />
							<input
								{...register('companyName')}
								placeholder="Best National Carports"
							/>
						</div>
					</label>

					<label className="client-hub-form__field">
						<span>Email</span>
						<div className="client-hub-form__input">
							<Mail size={16} />
							<input
								{...register('email')}
								placeholder="client@email.com"
								type="email"
							/>
						</div>
					</label>

					<label className="client-hub-form__field">
						<span>Phone Number</span>
						<div className="client-hub-form__input">
							<Phone size={16} />
							<input
								value={phoneNumber}
								onChange={handlePhoneChange}
								placeholder="(555) 555-5555"
								maxLength={14}
							/>
							<input
								type="hidden"
								{...register('phoneNumber', {
									validate: (value) =>
										exactDuplicate?.id ||
										removePhoneFormatting(value).length === 10 ||
										'Phone number must have 10 digits',
								})}
							/>
						</div>
						{errors.phoneNumber && <p>{errors.phoneNumber.message}</p>}
					</label>

					<label className="client-hub-form__field client-hub-form__field--full">
						<span>Street Address</span>
						<div className="client-hub-form__input">
							<MapPin size={16} />
							<input
								{...addressField}
								ref={(node) => {
									addressField.ref(node);
									attachInputRef(node);
								}}
								placeholder="123 Main St"
							/>
						</div>
						{autocompleteError && <p>{autocompleteError} You can still type it manually.</p>}
					</label>

					<label className="client-hub-form__field">
						<span>City</span>
						<div className="client-hub-form__input">
							<MapPin size={16} />
							<input {...register('city')} placeholder="Dallas" />
						</div>
					</label>

					<label className="client-hub-form__field">
						<span>State</span>
						<div className="client-hub-form__input">
							<MapPin size={16} />
							<input {...register('state')} placeholder="TX" />
						</div>
					</label>

					<label className="client-hub-form__field">
						<span>Zip Code</span>
						<div className="client-hub-form__input">
							<MapPin size={16} />
							<input {...register('postalCode')} placeholder="75001" />
						</div>
					</label>
				</div>

				<div className="client-hub-form__actions">
					<button type="button" onClick={onCancel} className="client-hub-btn client-hub-btn--ghost">
						{cancelLabel}
					</button>
					<button
						type="submit"
						className="client-hub-btn client-hub-btn--primary"
						disabled={isSubmitting}
					>
						{exactDuplicate
							? duplicateSubmitLabel
							: isSubmitting
								? submittingLabel
								: submitLabel}
					</button>
				</div>
			</form>
		</div>
	);
}
